import { MeQuery } from '@graphql/queries/__generated__/me.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Theme } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import makeStyles from '@mui/styles/makeStyles'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AccountCircleIcon from '../../../../icons/AccountCircleIcon'
import FavoriteIcon from '../../../../icons/FavoriteIcon'
import FavoriteIconFilled from '../../../../icons/FavoriteIconFilled'
import ReceiptLongIcon from '../../../../icons/ReceiptLongIcon'
import ShoppingCartIcon from '../../../../icons/ShoppingCartIcon'
import ShoppingCartIconFilled from '../../../../icons/ShoppingCartIconFilled'
import SpaceDashboardIcon from '../../../../icons/SpaceDashboardIcon'
import { ACCOUNT_LOGIN_URL, CART_URL, PROFILE_FAVORITES_URL, PROFILE_REWARDS_URL } from '../../../urls'
import HeaderCart from './HeaderCart'
import HeaderFavorites from './HeaderFavorites'
import { WholesaleAppStatusEnum } from '../../../../generated/gqlTypes'
import { useTranslation } from 'react-i18next'
import HeaderProfilePanel from '@components/Header/components/HeaderProfilePanel'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import Tooltip from '@mui/material/Tooltip'
import HeaderCurrencyPanel from '@components/Header/components/HeaderCurrencyPanel'
import { getCurrencySymbol } from '../../../utils/misc'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { LOCAL_STORAGE_KEYS } from '../../../../constants'

const useStyles = makeStyles((theme: Theme) => ({
  item: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    textDecoration: 'none',
    color: theme.palette.secondary.main,
    transition: 'color .15s ease-in-out',
    '& svg:last-of-type': {
      display: 'none',
    },
    '&:hover': {
      color: theme.palette.warning.main,
      '& svg:last-of-type': {
        display: 'block',
      },
      '& svg:first-of-type': {
        display: 'none',
      },
    },
  },
  cartBadge: {
    position: 'absolute',
    // From the middle of the link, where the 24px cart icon is centered, rather than from its top: the link is as
    // tall as the header row, so a fixed top drifts away from the icon. The badge sits on the basket's upper-right
    // corner.
    top: 'calc(50% - 20px)',
    left: '50%',
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #fff',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '-0.3px',
  },
  cartBadgeLong: {
    fontSize: 8,
  },
  currencyLabel: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  accountMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  cartWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  favoritesMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  currencyMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  panel: {
    padding: '24px',
    cursor: 'initial',
    width: 360,
    boxShadow: '0 6px 12px rgb(0 0 0 / 6%)',
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    top: '100%',
    right: 0,
    borderTop: '1px solid #d8d8d8',
    backgroundColor: '#fff',
    transition: 'all .1s ease-in-out',
    zIndex: 1,
  },
  panelCart: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  panelFavorites: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  panelCurrency: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))

interface IProps {
  user: MeQuery['me'] | null
  isStaff?: boolean
  cartCount: number
  isWholesaleMode: boolean

  handleClickBag(e: any): void

  handleClickSignIn(e: any): void

  setIsActiveOverlay(val: boolean): void

  handleClickContactUs(): void

  handleClickWSMode(): void
}

const HeaderUtilities: React.FC<IProps> = ({
  user,
  isStaff,
  cartCount,
  handleClickBag,
  handleClickSignIn,
  setIsActiveOverlay,
  handleClickContactUs,
  handleClickWSMode,
  isWholesaleMode,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  // Tracked via onMouseEnter/onMouseLeave rather than usehooks-ts `useHover`: that one binds its listeners
  // once, on mount, and the favorites/currency items only mount after `me` loads - so it never fired for them
  const [isHoverFavorites, setIsHoverFavorites] = useState(false)
  const [isHoverCurrency, setIsHoverCurrency] = useState(false)
  const [isHoverBag, setIsHoverBag] = useState(false)
  const hoverHandlers = (setIsHover: (val: boolean) => void, withOverlay: boolean) => ({
    onMouseEnter: () => {
      setIsHover(true)
      if (withOverlay) setIsActiveOverlay(true)
    },
    onMouseLeave: () => {
      setIsHover(false)
      if (withOverlay) setIsActiveOverlay(false)
    },
  })

  const [, , getStorageCurrency] = useLocalStorage(LOCAL_STORAGE_KEYS.CURRENCY, 'USD')

  const canShowWholesaleMode =
    user?.isStaff || user?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed
  const isImpersonate = user?.isImpersonate

  return (
    <ul className='utilities-list list-unstyled h-full items-center'>
      {user?.isStaff && (
        <li
          className={`relative utilities-list-item utilities-favorites hidden-on-affix hidden lg:flex ${classes.currencyMenuWrapper}`}
          {...hoverHandlers(setIsHoverCurrency, !smDown)}
        >
          <div className={`${classes.item} ${classes.currencyLabel} cursor-pointer`}>
            {`${getCurrencySymbol(getStorageCurrency())}/EN`}
          </div>
          <div className={`${classes.panel} ${classes.panelCurrency}`}>
            <HeaderCurrencyPanel isHover={isHoverCurrency} />
          </div>
        </li>
      )}
      {isImpersonate && (
        <li
          className='utilities-list-item hidden md:flex'
          onClick={() => (window.location.href = '/impersonate/stop/')}
        >
          <div className={classes.item}>
            <PersonOffIcon />
            <PersonOffIcon />
          </div>
        </li>
      )}
      {canShowWholesaleMode && (
        <li className={`utilities-list-item utilities-search flex`} onClick={handleClickWSMode}>
          <Tooltip
            title={t('header.wsMode', 'WS mode')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <div className={classes.item}>
              {isWholesaleMode && (
                <>
                  <CloseIcon />
                  <CloseIcon />
                </>
              )}
              {!isWholesaleMode && (
                <>
                  <ReceiptLongIcon viewBox='0 -960 960 960' />
                  <ReceiptLongIcon viewBox='0 -960 960 960' />
                </>
              )}
            </div>
          </Tooltip>
        </li>
      )}
      {!user && (
        <li className={`utilities-list-item hidden-on-affix utilities-account hidden md:flex`}>
          <Tooltip
            title={t('signIn', 'Sign In')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <Link to={ACCOUNT_LOGIN_URL} onClick={handleClickSignIn} className={classes.item}>
              <AccountCircleIcon viewBox='0 -960 960 960' />
              <AccountCircleIcon viewBox='0 -960 960 960' />
            </Link>
          </Tooltip>
        </li>
      )}

      {isStaff && (
        <li className='utilities-list-item utilities-dashboard relative header-dashboard dashboard-vs hidden sm:flex'>
          <Tooltip
            title={t('header.dashboard', 'Dashboard')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <a aria-label='Dashboard' className={`dashboard-button analytics-data ${classes.item}`} href='/dashboard/'>
              <SpaceDashboardIcon viewBox='0 -960 960 960' />
              <SpaceDashboardIcon viewBox='0 -960 960 960' />
            </a>
          </Tooltip>
        </li>
      )}
      {user && (
        <li
          className={`relative utilities-list-item utilities-favorites hidden-on-affix hidden sm:flex ${classes.favoritesMenuWrapper}`}
          {...hoverHandlers(setIsHoverFavorites, !smDown)}
        >
          <Link
            to={PROFILE_FAVORITES_URL}
            aria-label='Favorites'
            className={`favorites-button analytics-data ${classes.item}`}
            data-analytics_interaction_name='favorites'
            data-analytics_interaction_desc='favorites_header'
            data-analytics_event='click'
          >
            <FavoriteIcon viewBox='0 -960 960 960' />
            <FavoriteIconFilled viewBox='0 -960 960 960' />
          </Link>
          <div className={`${classes.panel} ${classes.panelFavorites}`}>
            <HeaderFavorites isHover={isHoverFavorites} />
          </div>
        </li>
      )}
      {user && (
        <li
          className={`relative utilities-list-item hidden-on-affix utilities-account hidden md:flex ${classes.accountMenuWrapper}`}
          onMouseEnter={() => setIsActiveOverlay(true)}
          onMouseLeave={() => setIsActiveOverlay(false)}
        >
          <Link to={PROFILE_REWARDS_URL} onClick={user ? undefined : handleClickSignIn} className={classes.item}>
            <AccountCircleIcon viewBox='0 -960 960 960' />
            <AccountCircleIcon viewBox='0 -960 960 960' />
          </Link>
          <div className={classes.panel}>
            <HeaderProfilePanel user={user} handleClickContactUs={handleClickContactUs} />
          </div>
        </li>
      )}
      <li
        className={`utilities-list-item utilities-cart relative flex ${classes.cartWrapper}`}
        {...hoverHandlers(setIsHoverBag, !smDown)}
      >
        <Link
          to={CART_URL}
          onClick={!cartCount ? handleClickBag : null}
          aria-label='Cart'
          className={`relative bag-button ${classes.item}`}
        >
          <ShoppingCartIcon viewBox='0 -960 960 960' />
          <ShoppingCartIconFilled viewBox='0 -960 960 960' />
          {cartCount > 0 && (
            <span className={`${classes.cartBadge} ${cartCount > 99 ? classes.cartBadgeLong : ''}`}>
              {cartCount > 99 ? '99+' : cartCount}
            </span>
          )}
        </Link>
        <div className={`${classes.panel} ${classes.panelCart}`}>
          <HeaderCart isHover={isHoverBag} />
        </div>
      </li>
    </ul>
  )
}

export default HeaderUtilities
